import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import { useSelector } from '@xstate/react'
import { take } from 'lib/array/arrayUtils'
import Caption from 'LuxKit/Typography/Caption'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import SharedCartsUserAvatar from './SharedCartsUserAvatar'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { isSharedMultiCart } from 'lib/userCart/multiCartVisibility'

const AvatarGroup = styled(Group)`
  > * + * {
    margin-inline-start: -${rem(8)};
  }
`

const WhiteCircleAvatar = styled.div`
  width: ${rem(28)};
  height: ${rem(28)};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground};
`

const MAX_USERS_TO_SHOW = 3

interface Props {
  activeCart: App.MultiCartSummary
  showPlusIcon?: boolean
}

function SharedCartsUserAvatarGroup(props: Props) {
  const { activeCart, showPlusIcon = false } = props
  const intl = useIntl()

  // This modal is getting opened from Snackbar which is out of context of MachineContextProvider.
  // hence we use getCartMachineActor() to get the actor ref else MultiCartContext.useActorRef() is preferred.
  const multiCartActorRef = getCartMachineActor()
  if (!multiCartActorRef) {
    throw new Error('Multi cart actor ref not found')
  }

  const cartMembers = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartMembers)
  const currentCartMembers = cartMembers.status === 'loaded' ? cartMembers.data[activeCart.cartId] ?? [] : []
  const activeCartMembers = currentCartMembers.filter(member => member.accessStatus !== 'revoked' && member.accessStatus !== 'pending')
  const cartMembersCount = activeCartMembers.length

  if (cartMembers.status !== 'loaded' || !isSharedMultiCart(activeCart)) {
    return null
  }

  return (
    <AvatarGroup direction="horizontal" verticalAlign="center">
      {cartMembersCount <= MAX_USERS_TO_SHOW && <>
        {activeCartMembers.map((member) => (
          <SharedCartsUserAvatar key={member.userId} cartMember={member} />
        ))}
        {cartMembersCount < MAX_USERS_TO_SHOW && showPlusIcon && (
          <WhiteCircleAvatar>
            <LinePlusIcon size="XS" />
          </WhiteCircleAvatar>
        )}
      </>}
      {cartMembersCount > MAX_USERS_TO_SHOW && <>
        {take(activeCartMembers, MAX_USERS_TO_SHOW - 1).map((member) => (
          <SharedCartsUserAvatar key={member.userId} cartMember={member} />
        ))}
        <WhiteCircleAvatar>
          <Caption variant="small" weight="bold">
            {intl.formatMessage(
              {
                id: '9V_PNBTSxOSvNdxu_TVu',
                defaultMessage: '+{count}',
              },
              { count: cartMembersCount - MAX_USERS_TO_SHOW + 1 },
            )}
          </Caption>
        </WhiteCircleAvatar>
      </>}
    </AvatarGroup>
  )
}

export default SharedCartsUserAvatarGroup
